import {useEffect} from 'react'
import {useGTMDispatch} from '../analytics/react-gtm-hook/index'
import {useDataCloudDispatch} from './data-cloud'
import {useCustomerStore} from '../store/hooks/useStore'
import {useBasketStore} from '../store/hooks/useStore'
import {getDeviceType} from '../utils/utils'
import {normalizePhoneNumberToE164} from '../utils/phone-utils'
import {generateCodeChallenge} from 'commerce-api/src/pkce'
import { EncodeType } from 'commerce-api/dist/types/utils'

/**
 * User data tracking hook, watches changes to customer object and 
 * updates data layer in response
 * @returns 
 */
export const useUserDataTracking = () => {

  const {customerInfo, status } = useCustomerStore()
  const sendDataToGTM = useGTMDispatch()
  const sendDataToDataCloud = useDataCloudDispatch()
  const {deliveryPostalCode, currentDeliveryStoreId} = useBasketStore()

  useEffect(() => {

    if(status != 'done') return

    setTimeout(() => {
      const sendUserData = async () => {
        try {
          const userData = await createUserData(
            {
              customerInfo: customerInfo, 
              postCode: deliveryPostalCode,
              storeId: currentDeliveryStoreId
            }
          )
          sendDataToGTM(userData)
          sendDataToDataCloud(userData)
        } catch (error) { 
          console.error('useUserDataTracking userEffect error: ' + error.message)
        }
      }
      sendUserData()
    }, 0)

  }, [customerInfo, status, deliveryPostalCode, currentDeliveryStoreId, sendDataToGTM, sendDataToDataCloud])

  return null
}

/**
 * 
 * @param {*} sourceData Source customer info from Customer store
 * @returns 
 */
const createUserData = async (sourceData) => {
  const {customerInfo, postCode, storeId } = sourceData

  const hashSensitiveData = async (data) => {
    const encodeType = EncodeType.HEX
    const fieldsToHash = [
      'first_name',
      'last_name',
      'email_address',
      'phone',
      'street',
      'city',
      'region',
      'country'
    ]

    const hashedData = {...data}

    for (const field of fieldsToHash) {
      if (data.logged_in !== 'Logged Out' && data[field]) {
        // Hash the field if the user is logged in
        hashedData[`sha256_${field}`] = await generateCodeChallenge(data[field], encodeType)
      } else if (['street', 'city', 'region', 'country'].includes(field)) {
        // Rename these fields for consistency, although they will be empty strings
        hashedData[`sha256_${field}`] = data[field]
      }

      // Remove the original unhashed field
      delete hashedData[field] 
    }

    hashedData.sha256_postal_code = data.postal_code ? await generateCodeChallenge(data.postal_code, encodeType) : data.postal_code
    delete hashedData.postal_code

    return hashedData
  }

  const customerId = (customerInfo?.customerNo == undefined) 
    ? customerInfo.customerId 
    : customerInfo.customerNo

  const address = redactAddressBook(customerInfo.addresses)

  const userData = {
    'event' :'userData',
    customer_id: customerId,  
    user_id: customerInfo.customerNo,
    logged_in: customerInfo.authType === 'registered' ? 'Logged In' : 'Logged Out',
    registered: customerInfo.authType === 'registered' ? 'Yes' : 'No',
    first_name: customerInfo.firstName,
    last_name: customerInfo.lastName,
    email_address: customerInfo.login,
    phone: preferredAddressPhone(customerInfo.addresses) ?? customerInfo.phoneHome,
    postal_code: postCode,
    country: address ? address.countryCode : '',
    store_id: storeId,
    device_type: getDeviceType(window?.navigator?.userAgent),
    order_count: customerInfo.c_ordersCount,
    customer_status: calculateCustomerStatus(customerInfo),
    street: address ? address.street : '',
    city: address ? address.city : '',
    region: address ? address.region : ''
  }

  const hashedUserData = await hashSensitiveData(userData)
  return hashedUserData
}

/**
 * Calculate a customer status classification
 * @param {*} customerInfo 
 * @returns String status description of customer
 */
const calculateCustomerStatus = (customerInfo) => {
 
  if(customerInfo.authType != 'registered') return 'unknown'

  const orderCount = (customerInfo?.c_ordersCount != undefined) ? customerInfo?.c_ordersCount : 0
  const legacyOrderCount = (customerInfo?.c_legacyOrderCount != undefined) ? customerInfo?.c_legacyOrderCount : 0   

  return (orderCount > 0 || legacyOrderCount > 0) ? 'existing' : 'new'

}

/**
 * Parse address book and return shorthand address for advanced conversion
 * @param {*} addresses address book to the evaluate
 * @returns object of shortened customer preferred address object
 */
const redactAddressBook = (addresses) => { 

  if(!addresses?.length > 0) return undefined

  let preferredAddress

  for(let address of addresses) {
    if (address.preferred) {
      preferredAddress = {
        street: address.address1 + (address.address2 ? ` ${address.address2}` : ''),
        city: address.city,
        region: address.stateCode,
        countryCode: address.countryCode
      }
      break
    }
  }

  return preferredAddress
}

/**
 * Parse address book and return phone number from preferred (primary) address
 * @param {*} addresses address book to evaluate
 * @returns the phone number from preferred address
 */
const preferredAddressPhone = (addresses) => { 

  if (!addresses?.length > 0) return undefined

  let preferredPhone

  for (let address of addresses) {
    if (address.preferred) {
      preferredPhone = address.phone
      break
    }
  }

  return normalizePhoneNumberToE164(preferredPhone)
}